import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { useClickAnyWhere } from 'usehooks-ts';
import More from '@/assets/images/icons/more.svg?react';
import type { ModalAction } from '@/components/common/modal/modal.type';
import useDeleteTags from '@/hooks/tags/use-delete-tags';
import { useTagById } from '@/hooks/tags/use-tags';
import useUpdateTag from '@/hooks/tags/use-update-tag';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';
import TagNameModal from './tag-name-modal';

interface Props {
  className?: string;
}

export default function TagHeaderMenu({ className }: Props) {
  const { tagId } = useTypedParams(ROUTES.CONTACT.TAGS.BY_ID);
  const { data } = useTagById(tagId);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const detailRef = useRef<HTMLDetailsElement>(null);
  const tagFormRef = useRef<ModalAction>(null);
  const { mutateAsync: updateTag } = useUpdateTag();
  const { mutateAsync: deleteTag } = useDeleteTags();

  const closeMenu = () => detailRef.current?.hasAttribute('open') && detailRef.current?.removeAttribute('open');

  useClickAnyWhere(closeMenu);

  const handleDeleteTag = () => {
    makePrompt({
      key: 'delete-tag',
      title: t('contact.tags.delete.delete-tag'),
      content: t('contact.tags.delete.confirm-message'),
      promise: async () => {
        const res = await deleteTag([tagId]);
        if (res.Code === 0) {
          navigate(-1);
        }
      },
    }).catch(console.warn);
  };

  const handleUpdateTag = async (tagName: string) => {
    const res = await updateTag({
      TagId: tagId,
      TagName: tagName,
    });
    if (res.Code === 0) {
      tagFormRef.current?.close();
    }
  };

  return (
    <details
      className={cn('dropdown dropdown-end', className)}
      ref={detailRef}>
      <summary
        tabIndex={0}
        role='button'
        className='btn btn-square mode-embedded:btn-sm btn-ghost mode-embedded:text-primary-content'>
        <More className='size-4 rotate-90' />
      </summary>

      <div className='menu dropdown-content z-50 rounded-lg p-0 min-w-22 bg-neutral-0 shadow-lg w-24'>
        <button
          type='button'
          className='whitespace-nowrap btn btn-ghost text-neutral-content'
          onClick={() => tagFormRef.current?.open()}>
          {t('contact.tags.change-name')}
        </button>

        <button
          type='button'
          className='whitespace-nowrap btn btn-ghost text-red-600'
          onClick={handleDeleteTag}>
          {t('contact.tags.delete.delete-tag')}
        </button>
      </div>

      <TagNameModal
        ref={tagFormRef}
        type='edit'
        onSubmit={handleUpdateTag}
        defaultValue={data?.TagName || ''}
      />
    </details>
  );
}
